import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Search, Plus, Menu } from 'lucide-react'
import { type ReactNode } from 'react'
import { useSidebar } from '@/components/ui/sidebar'
import { CalendarIcon } from './Icons'

interface SiteHeaderProps {
  title: string
  subtitle?: string
  showSearch?: boolean
  searchPlaceholder?: string
  buttonText?: string
  buttonIcon?: ReactNode
  onButtonClick?: () => void
  onSearchChange?: (value: string) => void
  searchValue?: string
  showDatePicker?: boolean
  dateRangeText?: string
  onDatePickerClick?: () => void
}

export function SiteHeader({
  title,
  subtitle,
  showSearch = true,
  searchPlaceholder = 'Search...',
  buttonText,
  buttonIcon = <Plus className="h-5 w-5" aria-hidden="true" />,
  onButtonClick,
  onSearchChange,
  searchValue = '',
  showDatePicker = false,
  dateRangeText = 'Select date range',
  onDatePickerClick,
}: Readonly<SiteHeaderProps>) {
  const { toggleSidebar } = useSidebar()

  return (
    <header
      className="flex flex-col gap-4 p-4 pb-1 bg-transparent rounded-tl-xl rounded-tr-xl"
      aria-label="Site header"
    >
      <div className="flex flex-col items-start lg:flex-row lg:items-center justify-between gap-4 flex-wrap ">
        {/* Mobile Hamburger + Title Section */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Hamburger Menu - Visible from lg breakpoint and below */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebar}
            className="lg:hidden h-8 w-8 p-0"
            aria-label="Toggle sidebar"
          >
            <Menu className="h-5 w-5 text-ss-neutral-950" aria-hidden="true" />
          </Button>

          <div className="flex flex-col gap-0.5 flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-ss-neutral-950 leading-8">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-ss-neutral-600 leading-4.5 ">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Actions Section */}
        <div className="flex items-center gap-3 flex-wrap">
          {showSearch && (
            <div className="relative w-[230px]">
              <Search
                id="main-content"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-ss-neutral-600"
                aria-hidden="true"
              />
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={e => {
                  onSearchChange?.(e.target.value)
                }}
                className="pl-10 h-10 rounded-lg bg-white text-ss-neutral-900 border-ss-neutral-200 
        focus:border-ss-primary-500 focus:ring-1 focus:ring-ss-primary-500
        placeholder:text-ss-neutral-400"
                aria-label="Search input"
              />
            </div>
          )}

          {showDatePicker && (
            <Button
              onClick={onDatePickerClick}
              variant="outline"
              aria-label="Select date range"
            >
              <CalendarIcon className="h-5 w-5" aria-hidden="true" />
              {dateRangeText}
            </Button>
          )}

          {buttonText && (
            <Button
              onClick={onButtonClick}
              variant="default"
              aria-label={buttonText}
            >
              {buttonIcon}
              {buttonText}
            </Button>
          )}
        </div>
      </div>
    </header>
  )
}
